import type { NavigateFunction } from 'react-router-dom';
import type { ProductId } from '@/config/products';
import type { WorkspaceContextPayload } from '@/config/productRegistry';
import type { User } from '@/types';
import { getApiBaseUrl } from '@/utils/apiBase';
import { resolveAuthenticatedHomeAfterAuth } from '@/utils/productLicense';

/** When false, post-login routing matches legacy behavior (Plan-only prod safe). */
export function isWorkspaceRouterEnabled(): boolean {
  return import.meta.env.VITE_USE_WORKSPACE_ROUTER === 'true';
}

async function fetchWorkspaceContext(accessToken: string): Promise<WorkspaceContextPayload | null> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/workspace/context`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) return null;
    const json = await res.json();
    return (json?.data ?? null) as WorkspaceContextPayload | null;
  } catch {
    return null;
  }
}

/**
 * Navigate after successful auth — optionally uses workspace resolver API.
 * Falls back to legacy routing if flag off or API fails.
 */
export async function navigateAfterLogin(
  navigate: NavigateFunction,
  authUser: User,
  entryProductId: ProductId,
  accessToken: string,
): Promise<void> {
  const route = await resolveAuthenticatedHomeAfterAuth({
    settings: authUser.tenant?.settings as Record<string, unknown> | undefined,
    role: authUser.role,
    entryProductId,
    accessToken,
  });

  navigate(route, { replace: true });
}
